import { db } from '../config/firebase';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  runTransaction,
} from 'firebase/firestore';
import { Recipe } from '../types/recipe';
import { normalizeCodeKey } from '../utils/recipeCukcukSync';

const COLLECTION_NAME = '8am_recipes';
const normalizeItemId = normalizeCodeKey;
const withoutUndefined = <T extends Record<string, unknown>>(value: T): T =>
  Object.fromEntries(Object.entries(value).filter(([, fieldValue]) => fieldValue !== undefined)) as T;
const identifiers = (recipe: Pick<Recipe, 'itemId' | 'itemIdAliases'>) =>
  Array.from(new Set([recipe.itemId, ...(recipe.itemIdAliases || [])].map(normalizeItemId).filter(Boolean)));
const fromSnapshot = (snapshot: Awaited<ReturnType<typeof getDocs>>): Recipe[] =>
  snapshot.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Recipe, 'id'>) }));

const assertNoCollisions = (recipes: Array<Omit<Recipe, 'id'> & { id?: string }>, existing: Recipe[]) => {
  const owners = new Map<string, string>();

  for (const recipe of existing) {
    if (!recipe.id || recipes.some(({ id }) => id === recipe.id)) continue;
    for (const key of identifiers(recipe)) owners.set(key, recipe.id);
  }

  for (const recipe of recipes) {
    const owner = recipe.id || '';
    for (const key of identifiers(recipe)) {
      const collision = owners.get(key);
      if (collision !== undefined && collision !== owner) throw new Error(`Recipe item ID collision: ${key}`);
      owners.set(key, owner);
    }
  }
};

export const recipeService = {
  async getAllRecipes(): Promise<Recipe[]> {
    return fromSnapshot(await getDocs(collection(db, COLLECTION_NAME)));
  },

  async getRecipeByItemId(itemId: string): Promise<Recipe | null> {
    const key = normalizeItemId(itemId);
    const recipes = await this.getAllRecipes();
    const canonical = recipes.filter((recipe) => normalizeItemId(recipe.itemId) === key);
    if (canonical.length > 1) throw new Error(`Ambiguous recipe item ID: ${key}`);
    if (canonical.length === 1) return canonical[0];

    const aliases = recipes.filter((recipe) =>
      recipe.itemIdAliases?.some((alias) => normalizeItemId(alias) === key),
    );
    if (aliases.length > 1) throw new Error(`Ambiguous recipe item ID: ${key}`);
    return aliases[0] || null;
  },

  /**
   * Lưu công thức. Ưu tiên update theo `id` (đổi itemId khi sync CUKCUK),
   * không có id thì upsert theo itemId.
   */
  async saveRecipe(recipe: Omit<Recipe, 'id'> & { id?: string }): Promise<Recipe> {
    const now = new Date().toISOString();
    const { id, ...payload } = recipe;
    const firestorePayload = withoutUndefined(payload);
    const existing = await this.getAllRecipes();
    let targetId = id;

    if (!targetId) {
      const key = normalizeItemId(recipe.itemId);
      const canonical = existing.filter((item) => normalizeItemId(item.itemId) === key);
      if (canonical.length > 1) throw new Error(`Ambiguous recipe item ID: ${key}`);
      targetId = canonical[0]?.id;
    }

    assertNoCollisions([{ id: targetId, ...firestorePayload }], existing);

    if (targetId) {
      await updateDoc(doc(db, COLLECTION_NAME, targetId), { ...firestorePayload, updatedAt: now });
      return { id: targetId, ...firestorePayload };
    }

    const docRef = await addDoc(collection(db, COLLECTION_NAME), {
      ...firestorePayload,
      createdAt: now,
      updatedAt: now,
    });
    return { id: docRef.id, ...firestorePayload };
  },

  async batchUpdateById(recipes: Array<Omit<Recipe, 'id'> & { id?: string }>): Promise<void> {
    if (recipes.some((recipe) => !recipe.id)) throw new Error('Recipe ID is required for batch update');
    const ids = recipes.map((recipe) => recipe.id!);
    const duplicateId = ids.find((id, index) => ids.indexOf(id) !== index);
    if (duplicateId) throw new Error(`Duplicate recipe ID in batch: ${duplicateId}`);

    const listed = await this.getAllRecipes();
    const listedIds = Array.from(new Set([...listed.map((recipe) => recipe.id).filter((id): id is string => !!id), ...ids]));
    await runTransaction(db, async (transaction) => {
      const snapshots = await Promise.all(listedIds.map((id) => transaction.get(doc(db, COLLECTION_NAME, id))));
      const existing = snapshots
        .filter((snapshot) => snapshot.exists())
        .map((snapshot) => ({ id: snapshot.id, ...(snapshot.data() as Omit<Recipe, 'id'>) }));
      const missingId = ids.find((id) => !existing.some((recipe) => recipe.id === id));
      if (missingId) throw new Error(`Recipe not found: ${missingId}`);

      const finalRecipes = existing.map((recipe) => recipes.find(({ id }) => id === recipe.id) || recipe);
      assertNoCollisions(finalRecipes, []);
      const updatedAt = new Date().toISOString();
      recipes.forEach(({ id, itemId, itemIdAliases, itemName, optionAddons }) => {
        transaction.update(doc(db, COLLECTION_NAME, id!), withoutUndefined({
          itemId,
          itemIdAliases,
          itemName,
          optionAddons,
          updatedAt,
        }));
      });
    });
  },

  async deleteRecipe(id: string): Promise<void> {
    await deleteDoc(doc(db, COLLECTION_NAME, id));
  },
};
